import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { useNavigate } from "react-router-dom";
import "../../App.css";
import Sidebar from '../../Sidebar';
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import { displayError } from "../../helpers";
import { openPdfResponse } from "../../utils/openPdfInApp";
import { getAllSalesOrder, deleteSalesOrder,printSalesOrder,printProformaInvoice} from "../../services/SalesOrderServices";
import { buildSalesOrderQueryParams } from "../../utils/listViewDateRanges";
import { getDefaultListViewConfig } from "../../config/listViewSettingsConfig";
import { bootstrapListViewLoad } from "../../utils/listViewBootstrap";
import ListFormPageHeader from "../../components/ListFormPageHeader";
import ListPageGradientSummary from "../../components/ListPageGradientSummary";
import { buildGradientCard, formatQty, sumField, splitRecordsByStatus, buildCancelledSummaryCard } from "../../utils/listSummaryUtils";
import moment from "moment";

function SalesOrderTable() {
    const InitialData = {
        total_pages: 0,
        records: [],
      };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate(); 
    const [refreshKey, setRefreshKey] = useState(0);
    const [cookies] = useCookies(['myToken']);
    const token = cookies.myToken;
    const [isLoading, setIsLoading] = useState(true);
    const requestIdRef = useRef(0);
    const hasRowsRef = useRef(false);

    const right_pinned = ["id"];
    const left_pinned = ['order_no', 'order_date'];
    const todayDateLabel = moment().format('DD-MM-YYYY');

    const { active: activeRecords, cancelled: cancelledRecords } = useMemo(
        () => splitRecordsByStatus(data.records),
        [data.records],
    );

    const highlightCard = useMemo(() => {
        const todayRecords = activeRecords.filter((row) => row.order_date === todayDateLabel);
        return buildGradientCard('Today SO', formatQty(sumField(todayRecords, 'quantity')), 'today');
    }, [activeRecords, todayDateLabel]);

    const summaryCards = useMemo(() => {
        const cards = [
            buildGradientCard('Order Qty', formatQty(sumField(activeRecords, 'quantity')), 'blue'),
            buildGradientCard('Scheduled', formatQty(sumField(activeRecords, 'scheduled_qty')), 'purple'),
            buildGradientCard('Supplied', formatQty(sumField(activeRecords, 'supplied_qty')), 'teal'),
        ];
        const cancelledCard = buildCancelledSummaryCard(cancelledRecords, {
            valueField: 'quantity',
            entityLabel: 'SO cancelled',
        });
        if (cancelledCard) cards.push(cancelledCard);
        return cards;
    }, [activeRecords, cancelledRecords]);

    const cancelledPopup = useMemo(() => ({
        title: `Cancelled Sales Orders (${cancelledRecords.length})`,
        emptyMessage: 'No cancelled sales orders in the current list.',
        columns: [
            { key: 'order_no', label: 'SO No', align: 'center' },
            { key: 'order_date', label: 'SO Date', align: 'center' },
            { key: 'customer', label: 'Customer', align: 'left' },
            { key: 'consignee', label: 'Site', align: 'left' },
            { key: 'product', label: 'Grade', align: 'left' },
            { key: 'quantity', label: 'Order Qty', align: 'right' },
        ],
        getRows: () => cancelledRecords,
        mapRow: (row) => ({
            order_no: row.order_no,
            order_date: row.order_date,
            customer: row.customer,
            consignee: row.consignee,
            product: row.product,
            quantity: formatQty(row.quantity),
        }),
        footerRowBuilder: (rows) => ({
            order_no: 'Total',
            order_date: '',
            customer: '',
            consignee: '',
            product: `${rows.length} SO`,
            quantity: formatQty(sumField(rows, 'quantity')),
        }),
    }), [cancelledRecords]);

    const columns = useMemo(
        () => [
            { fieldName: 'order_no', headerName: 'SO No', size: 88 },
            { fieldName: "order_date", headerName: 'SO Date', size: 86 },
            { fieldName: "purchase_order_no", headerName: 'PO No', size: 78 },
            { fieldName: "purchase_order_date", headerName: 'PO Date', size: 86 },
            { fieldName: "salesrep", headerName: 'Marketing Name', size: 110 },
            { fieldName: "customer", headerName: 'Customer', size: 120 },
            { fieldName: "consignee", headerName: 'Site / Project', size: 120 },
            { fieldName: "product", headerName: 'Grade', size: 75 },
            { fieldName: "rate", headerName: 'Rate', size: 68, align: 'right' },
            { fieldName: "quantity", headerName: 'Order Qty', size: 78, align: 'right' },
            { fieldName: "scheduled_qty", headerName: 'Sch Qty', size: 72, align: 'right' },
            { fieldName: "supplied_qty", headerName: 'Sup Qty', size: 72, align: 'right' },
            { fieldName: "balance_qty", headerName: 'Bal Qty', size: 72, align: 'right' },
            { fieldName: "delivery_mode", headerName: 'Delivery Mode', size: 90 },
            { fieldName: "is_approved", headerName: 'Approval Status', size: 100 },
            { fieldName: "status", headerName: 'Status', size: 82 },
            { fieldName: "id", headerName: "ROW_ACTIONS" },
        ],
        [],
    );

    const onEditSalesOrder = (SalesOrderId, event) => {
       navigate(`/SalesOrderEdit/${SalesOrderId}/`);
    };

    const onDeleteSalesOrder = (SalesOrderId, event) => {
        event.preventDefault();
        Swal.fire({title: 'Are you sure to Delete?',  
        showCancelButton: true,  
        confirmButtonText: `Yes`,  
        cancelButtonText: `No`,
        }).then((result) => {  
          if (result.isConfirmed) { 

            setIsLoading(true);
            deleteSalesOrder(cookies, SalesOrderId)
            .then(response => {
                console.log(`Sales Order with id ${SalesOrderId} deleted`);
                setRefreshKey(oldKey => oldKey +1);
                Swal.fire('Deleted Successfully!', '', 'success');
            })
            .catch(error => {
                setRefreshKey(oldKey => oldKey +1);
                console.log(error.response.data);
                displayError(error.response.data,'Delete Failed');
            }); 
            
            
          } else if (result.isDismissed) {    
            Swal.fire('Not Deleted', '', 'info')  
        }
        });
        //

    };

    const handleCSVExport = (event) => {
        console.log('CSV Export');
        exportAsCSV(
            columns
            .filter(col => col.headerName !== 'ROW_ACTIONS')
            .map(c => c.headerName),
            data.records.map(salesorder => {

                const csvRow = {
                    order_no : salesorder.order_no,
                order_date: salesorder.order_date,
                purchase_order_no:salesorder.purchase_order_no,
                purchase_order_date:salesorder.purchase_order_date,
                customer: salesorder.consignee.customer.name,
                salesrep:salesorder.consignee.customer.salesrep.name,
                consignee : salesorder.consignee.name,
                product: salesorder.product.name,
                quantity:salesorder.quantity,
                rate : salesorder.rate,
                scheduled_qty: salesorder.scheduled_qty,
                supplied_qty:salesorder.supplied_qty,
                balance_qty : salesorder.balance_qty,
                delivery_mode: (salesorder.delivery_mode || '').toUpperCase(),
                is_approved:salesorder.is_approved,
                status:salesorder.status? "Active":"Inactive",
                }
                return csvRow;
                }));
    };

    const handlePDFExport = (event) => {
        console.log('PDF Export');
        exportAsPDF(
            'List of Sales Order Form',
            columns
            .filter(col => col.headerName !== 'ROW_ACTIONS')
            .map(col=>({header: col.headerName, dataKey: col.fieldName })),
            data.records.map(salesorder => ({
                order_no : salesorder.order_no,
                order_date: salesorder.order_date,
                purchase_order_no:salesorder.purchase_order_no,
                purchase_order_date:salesorder.purchase_order_date,
                customer: salesorder.consignee.customer.name,
                salesrep:salesorder.consignee.customer.salesrep.name,
                consignee : salesorder.consignee.name,
                product: salesorder.product.name,
                quantity:salesorder.quantity,
                rate : salesorder.rate,
                scheduled_qty: salesorder.scheduled_qty,
                supplied_qty:salesorder.supplied_qty,
                balance_qty : salesorder.balance_qty,
                delivery_mode: (salesorder.delivery_mode || '').toUpperCase(),
                is_approved:salesorder.is_approved,
                status:salesorder.status? "Active":"Inactive",
            })),
            'salesorder.pdf'
        );
    };

    const onPrintSalesOrder = (SalesOrderId, event) => {
        event.preventDefault();
        printSalesOrder(cookies, SalesOrderId)
        .then(response => {
            openPdfResponse(response, { title: 'Sales Order', downloadFileName: 'sales_order.pdf' });
        })
        .catch(error => {
            console.log(error);
            displayError(error,'Print Failed');
        });  
    };

    const onPrintproformaInvoice = (deliveryId, event) => {
        event.preventDefault();
        printProformaInvoice(cookies, deliveryId)
        .then(response => {
            openPdfResponse(response, { title: 'Proforma Invoice', downloadFileName: 'proforma_invoice.pdf' });
        })
        .catch(error => {
            console.log(error);
            displayError(error,'BR print Failed');
        });  
    };

    const loadSalesOrderList = useCallback((config, options = {}) => {
        if (!token) {
            setIsLoading(false);
            return Promise.resolve();
        }
        const activeConfig = config || getDefaultListViewConfig('sales_order');
        const requestId = ++requestIdRef.current;
        const keepRowsVisible = options.keepRowsVisible === true && hasRowsRef.current;
        if (!keepRowsVisible) {
            setIsLoading(true);
        }
        const params = {
            ...buildSalesOrderQueryParams(activeConfig),
            limit: 2000,
        };
        return getAllSalesOrder({ myToken: token }, params)
        .then( salesorderList => {
            if (requestId !== requestIdRef.current) return;
            const tableData = (salesorderList?.sales_order_list || [])
            .map(salesorder => ({
                id :salesorder.id,
                order_no : salesorder.order_no,
                order_date: salesorder.order_date,
                purchase_order_no:salesorder.purchase_order_no,
                purchase_order_date:salesorder.purchase_order_date,
                customer: salesorder.consignee?.customer?.name || '',
                salesrep:salesorder.consignee?.customer?.salesrep?.name || '',
                consignee : salesorder.consignee?.project_name || '',
                product: salesorder.product?.name || '',
                quantity:salesorder.quantity,
                rate : salesorder.rate,
                scheduled_qty: salesorder.scheduled_qty,
                supplied_qty:salesorder.supplied_qty,
                balance_qty : salesorder.balance_qty,
                delivery_mode: (salesorder.delivery_mode || '').toUpperCase(),
                is_approved:salesorder.is_approved,
                status:salesorder.status? "Active":"Cancelled",
                is_batch_report_needed:'Yes',
                hide_row_print:'No'
            }));

            hasRowsRef.current = tableData.length > 0;
            setData({
                total: tableData.length,
                records: tableData
            });
        })
        .catch((error) => {
            if (requestId !== requestIdRef.current) return;
            console.log(error, 'getAllSalesOrder failed');
            if (!keepRowsVisible) {
                hasRowsRef.current = false;
                setData({ total: 0, records: [] });
            }
            const errorData = error.response?.data ?? error.message;
            if (error.code === 'ECONNABORTED') {
                displayError('Sales order list timed out. Try a shorter date range in General Settings.', 'Request timed out');
                return;
            }
            if (errorData) {
                displayError(errorData, 'Failed to load sales orders');
            }
        })
        .finally(() => {
            if (requestId === requestIdRef.current) {
                setIsLoading(false);
            }
        });
    }, [token]);

    const handleSettingsSaved = useCallback((config) => {
        loadSalesOrderList(config);
    }, [loadSalesOrderList]);

    useEffect(() => {
        if (!token) {
            setIsLoading(false);
            return undefined;
        }
        let cancelled = false;
        bootstrapListViewLoad({
            cookies: { myToken: token },
            viewKey: 'sales_order',
            loadFn: loadSalesOrderList,
            isCancelled: () => cancelled,
        });
        return () => {
            cancelled = true;
        };
    }, [token, loadSalesOrderList, refreshKey]);
    
    return (

        <div id="outer-container"  className="App" > 
            <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
            <div id="page-wrap">
            <ListFormPageHeader
                module="MARKETING"
                title="List of Sales Orders"
                viewKey="sales_order"
                onSettingsSaved={handleSettingsSaved}
                formLink="/SalesOrder"
                addNewLabel="New Sales Order"
                onExportCSV={handleCSVExport}
                onExportPDF={handlePDFExport}
            />
            <ListPageGradientSummary
                highlightCard={highlightCard}
                cards={summaryCards}
                cancelledPopup={cancelledPopup}
            />
            <div className="container item-list-table-container">
                <div className="table-responsive">
                <ReactTableWrapper
                            title='List of Group Form'
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditSalesOrder}
                            onRowDelete={onDeleteSalesOrder}
                            onRowPrint={onPrintSalesOrder}
                            hidePrint ={false}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            isLoading={isLoading}
                            hideDelete={true}
                            onRowBrPrint={onPrintproformaInvoice}
                            left_pinned={left_pinned}
                            right_pinned={right_pinned}
                        />
                </div>
                </div> 


            </div>
</div>

        
    );

}



export default SalesOrderTable;